import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, type ReactNode } from "react";

import {
  getCurrentAccount,
  loginAccount,
  logoutAccount,
  patchPreferences,
  patchProfile,
  registerAccount,
} from "./api";
import { AccountContext, ACCOUNT_QUERY_KEY } from "./context";
import type {
  AccountSnapshot,
  LoginValues,
  PreferencesPatch,
  ProfilePatch,
  RegisterValues,
} from "./types";

export function AccountProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const accountQuery = useQuery({
    queryKey: ACCOUNT_QUERY_KEY,
    queryFn: ({ signal }) => getCurrentAccount(signal),
    retry: false,
    staleTime: 60_000,
  });
  const preferencesMutation = useMutation({
    mutationFn: patchPreferences,
    onSuccess: (response) => {
      queryClient.setQueryData<AccountSnapshot | null>(
        ACCOUNT_QUERY_KEY,
        (current) =>
          current
            ? {
                ...current,
                preferences: response.preferences,
              }
            : current,
      );
    },
  });
  const updatePreferences = useCallback(
    async (values: PreferencesPatch) => {
      await preferencesMutation.mutateAsync(values);
    },
    [preferencesMutation],
  );
  const setAccount = useCallback(
    (account: AccountSnapshot | null) => {
      queryClient.setQueryData<AccountSnapshot | null>(
        ACCOUNT_QUERY_KEY,
        account,
      );
    },
    [queryClient],
  );
  const login = useCallback(
    async (values: LoginValues) => {
      setAccount(await loginAccount(values));
    },
    [setAccount],
  );
  const register = useCallback(
    async (values: RegisterValues) => {
      return registerAccount(values);
    },
    [],
  );
  const logout = useCallback(async () => {
    await logoutAccount();
    setAccount(null);
    queryClient.removeQueries({ queryKey: ["account"] });
    queryClient.setQueryData<AccountSnapshot | null>(ACCOUNT_QUERY_KEY, null);
  }, [queryClient, setAccount]);
  const refreshAccount = useCallback(async () => {
    const account = await getCurrentAccount();
    setAccount(account);
  }, [setAccount]);
  const updateProfile = useCallback(
    async (values: ProfilePatch) => {
      setAccount(await patchProfile(values));
    },
    [setAccount],
  );

  return (
    <AccountContext.Provider
      value={{
        account: accountQuery.data ?? null,
        isLoading: accountQuery.isPending,
        isAuthenticated:
          accountQuery.data !== null && accountQuery.data !== undefined,
        login,
        register,
        logout,
        refreshAccount,
        updateProfile,
        updatePreferences,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
}
